import { useRef } from 'react';

interface PaginationState {
    page?: number;
    total?: number;
    pageSize: number;
}

interface StablePaginationResult {
    stableTotalRows: number;
    stablePage: number;
}

/**
 * Custom hook to provide stable pagination values during loading states.
 *
 * This hook prevents flickering in data grids by maintaining the last known
 * pagination values (total rows and current page) when data is being refetched.
 *
 * @param pagination - The current pagination state from the table
 * @param loading - Whether data is currently being loaded
 * @returns An object containing stableTotalRows and stablePage
 *
 * @example
 * const { stableTotalRows, stablePage } = useStablePagination(pagination, loading);
 */
export default function useStablePagination(
    pagination: PaginationState,
    loading: boolean
): StablePaginationResult {
    const lastKnownTotalRef = useRef<number | undefined>(undefined);
    const lastKnownPageRef = useRef<number>(1);

    // Update refs when we have valid pagination values
    if (pagination.total !== undefined && pagination.total > 0) {
        lastKnownTotalRef.current = pagination.total;
    }
    if (pagination.page !== undefined && pagination.page > 0) {
        lastKnownPageRef.current = pagination.page;
    }

    // Calculate stable values
    let stableTotalRows = pagination.total ?? 0;
    if (loading && pagination.total === 0) {
        stableTotalRows = lastKnownTotalRef.current ?? 0;
    }

    const stablePage =
        pagination.page && pagination.page > 0 ? pagination.page : (lastKnownPageRef.current ?? 1);

    return {
        stableTotalRows,
        stablePage,
    };
}
